import { useState, useEffect } from "react";
import * as ISchema from "@schema";

import { MemberPerson } from "@clockwise/web-commons/src/ui/admin-panel/billing-groups-members/billing-groups-members.type";
import { isError } from "@util/errorable.util";

export const BillingGroupActionType = {
  add: "ADD",
  remove: "REMOVE",
  invite: "INVITE",
};
export type BillingGroupAction = {
  actionLabel?: string;
  type: string;
  billingGroupId: string;
};

export type BillingGroupActionMap = {
  [k: string]: BillingGroupAction;
};

export const useSelectTable = (members: MemberPerson[], billingGroups: ISchema.IBillingGroup[]) => {
  // ~-~-~-~-~-~-~-
  // State
  // ~-~-~-~-~-~-~-
  const [selectedMembers, setSelectedMembers] = useState(<MemberPerson[]>[]);
  const [isSelectDialogShowing, setIsSelectDialogShowing] = useState(false);
  const [availableActions, setAvailableActions] = useState(<BillingGroupActionMap>{});
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);
  const [isSelectTableDisabled, setIsSelectTableDisabled] = useState(true);
  // ~-~-~-~-~-~-~-
  // LifeCycle
  // ~-~-~-~-~-~-~-
  useEffect(() => {
    updateAvailableActions(selectedMembers);
    if (isSelectDialogShowing && selectedMembers.length === 0) {
      setIsSelectDialogShowing(false);
    } else if (!isSelectDialogShowing && selectedMembers.length > 0) {
      setIsSelectDialogShowing(true);
    }
  }, [selectedMembers]);

  // check to see if user is admin of business or member of pro plan
  useEffect(() => {
    const isUserAllowedBulkAction = billingGroups.some((bg: ISchema.IBillingGroup) => {
      const bgSubscription = getSubscriptionFromBillingGroup(bg);
      if (!!bgSubscription && isSubscriptionProductTypeEnterprise(bgSubscription.subscription)) {
        return isUserAdmin(bg);
      }
      if (!!bgSubscription && isSubscriptionProductTypeBusiness(bgSubscription.subscription)) {
        return isUserAdmin(bg);
      }
      if (!!bgSubscription && isSubscriptionProductTypePro(bgSubscription.subscription)) {
        return isUserOnlyMember(bg);
      }
      return false;
    });
    isUserAllowedBulkAction && setIsSelectTableDisabled(false);
  }, [billingGroups]);

  // ~-~-~-~-~-~-~-
  // Checkbox functions
  // ~-~-~-~-~-~-~-

  const onSelectOneMember = (person: MemberPerson) => {
    const isPersonAlreadySelected = selectedMembers.some(
      (member) => member.targetCalendarId === person.targetCalendarId,
    );

    if (isPersonAlreadySelected) {
      //remove from list
      const newSelectedMemberList = selectedMembers.filter(
        (member) => member.targetCalendarId !== person.targetCalendarId,
      );
      setSelectedMembers(newSelectedMemberList);
    } else {
      //add to list
      setSelectedMembers((selectedMembers) => [...selectedMembers, person]);
    }
  };

  const onSelectAllMembers = () => {
    if (isSelectAllChecked) {
      setSelectedMembers([]);
    } else {
      setSelectedMembers(members);
    }
    setIsSelectAllChecked(!isSelectAllChecked);
  };

  // ~-~-~-~-~-~-~-
  // Billing Group and Subscription
  // ~-~-~-~-~-~-~-

  const getSubscriptionFromBillingGroup = (billingGroup: ISchema.IBillingGroup) => {
    if (!billingGroup.subscription || isError(billingGroup.subscription)) {
      return;
    }
    return billingGroup.subscription;
  };

  const isSubscriptionProductTypeBusiness = (subscription: ISchema.IPaymentSubscription) => {
    return (
      subscription.productType === ISchema.ProductTypeEnum.Business_NonStandard ||
      subscription.productType === ISchema.ProductTypeEnum.Business_Standard
    );
  };

  const isSubscriptionProductTypePro = (subscription: ISchema.IPaymentSubscription) => {
    return (
      subscription.productType === ISchema.ProductTypeEnum.Pro_Standard ||
      subscription.productType === ISchema.ProductTypeEnum.Pro_NonStandard
    );
  };

  const isSubscriptionProductTypeEnterprise = (subscription: ISchema.IPaymentSubscription) => {
    return (
      subscription.productType === ISchema.ProductTypeEnum.Enterprise_Standard ||
      subscription.productType === ISchema.ProductTypeEnum.Enterprise_NonStandard
    );
  };

  const isUserAdmin = (bg: ISchema.IBillingGroup) => {
    return (
      bg.adminOrgPersons.__typename === "OrgPersonList" &&
      bg.adminOrgPersons.list.findIndex((p) => p.isYou) >= 0
    );
  };

  const isUserOnlyMember = (bg: ISchema.IBillingGroup) => {
    return (
      bg.manualMembers.__typename === "BillingGroupManualMemberList" &&
      bg.manualMembers.list.findIndex(
        (p) => p.orgPerson.__typename === "OrgPerson" && p.orgPerson.isYou,
      ) >= 0
    );
  };

  const hasPermissionToAddAndRemove = (bg: ISchema.IBillingGroup) => {
    const billingGroupSubscription = getSubscriptionFromBillingGroup(bg);
    const isProductTypeEnterprise =
      billingGroupSubscription &&
      isSubscriptionProductTypeEnterprise(billingGroupSubscription.subscription);

    const isProductTypeBusiness =
      billingGroupSubscription &&
      isSubscriptionProductTypeBusiness(billingGroupSubscription.subscription);

    const isProductTypePro =
      billingGroupSubscription &&
      isSubscriptionProductTypePro(billingGroupSubscription.subscription);

    const isAdmin = isUserAdmin(bg);

    const isOnlyMember = isUserOnlyMember(bg);

    return (
      (isProductTypeEnterprise && isAdmin) ||
      (isProductTypeBusiness && isAdmin) ||
      (isProductTypePro && isOnlyMember)
    );
  };

  //only update the actions if the user is in a business plan and a member
  const updateAvailableActions = (newMembers: MemberPerson[]) => {
    if (newMembers.length > 0) {
      const isEveryoneUser = newMembers.every((member) => !!member.userId);

      const isEveryoneInBillingGroup = (billingGroupId: string) =>
        newMembers.every(
          (member) => member.billingGroup && member.billingGroup.id === billingGroupId,
        );
      const isNoOneInBillingGroup = (billingGroupId: string) =>
        newMembers.every(
          (member) =>
            member.billingGroup === undefined || member.billingGroup.id !== billingGroupId,
        );

      const newActions = billingGroups.reduce(
        (accumulator: BillingGroupActionMap, billingGroup) => {
          if (hasPermissionToAddAndRemove(billingGroup)) {
            const everyoneInBillingGroup = isEveryoneInBillingGroup(billingGroup.id);
            const noOneInBillingGroup = isNoOneInBillingGroup(billingGroup.id);
            const billingGroupId = billingGroup.id;

            if (isEveryoneUser && everyoneInBillingGroup) {
              accumulator[billingGroupId] = {
                type: BillingGroupActionType.remove,
                billingGroupId: billingGroup.id,
                actionLabel: `Remove from ${billingGroup.name}`,
              };
            } else if (noOneInBillingGroup && isEveryoneUser) {
              accumulator[billingGroupId] = {
                type: BillingGroupActionType.add,
                billingGroupId: billingGroup.id,
                actionLabel: `Add to ${billingGroup.name} `,
              };
            }
          }
          return accumulator;
        },
        {},
      );
      setAvailableActions(newActions);
    } else {
      setAvailableActions({});
    }
  };

  return {
    selectedMembers,
    setSelectedMembers,
    onSelectOneMember,
    onSelectAllMembers,
    isSelectDialogShowing,
    setIsSelectDialogShowing,
    availableActions,
    isSelectAllChecked,
    isSelectTableDisabled,
  };
};
